import { useState, useEffect, useCallback } from 'react';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  Timestamp,
  QueryConstraint,
  getDocs,
} from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { COLLECTION_NAMES, ActionItem, User, Rock, Issue, Metric } from '../types';

// Define filter interface for L10 Meeting
export interface ActionItemFilters {
  ownerId?: string;
  linkType?: 'all' | 'rock' | 'issue' | 'metric' | 'standalone';
  overdueOnly?: boolean;
  complete?: boolean;
  dropped?: boolean;
}

/**
 * Hook for managing Action Items data with Firestore for L10 Meeting
 * Enhanced with filtering capabilities for the L10 Meeting page
 * Uses Firestore v9+ modular API with real-time updates via onSnapshot
 */
export function useL10ActionItems(filters: ActionItemFilters) {
  const [actionItems, setActionItems] = useState<ActionItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    
    // Define the base query
    const actionItemsCollection = collection(db, COLLECTION_NAMES.ACTION_ITEMS);
    const queryConstraints: QueryConstraint[] = [];

    // 1. Apply Status Filters (Complete/Dropped)
    // Default to showing incomplete, non-dropped action items
    const completeFilter = filters.complete !== undefined ? filters.complete : false;
    queryConstraints.push(where('complete', '==', completeFilter));
    
    const droppedFilter = filters.dropped !== undefined ? filters.dropped : false;
    queryConstraints.push(where('dropped', '==', droppedFilter));

    // 2. Apply Owner Filter
    if (filters.ownerId && filters.ownerId !== 'all') {
      queryConstraints.push(where('ownerId', '==', filters.ownerId));
    }

    // 3. Apply Overdue Filter
    // Note: Firestore doesn't support date comparisons efficiently in queries
    // We'll filter overdue items client-side after fetching
    // The complete filter above already ensures we only get incomplete action items

    // 4. Apply Link Type Filter
    // Note: Firestore doesn't support '!=' queries efficiently on multiple fields
    // We'll apply link type filtering client-side
    // For now, we'll fetch all action items and filter client-side for link types

    // 5. Add Sorting by due date
    queryConstraints.push(orderBy('dueDate', 'asc'));

    const q = query(actionItemsCollection, ...queryConstraints);

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const actionItemsData = snapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Ensure description exists (use task as fallback for legacy data)
            description: data.description || data.task || '',
            assignedToUserId: data.assignedToUserId || data.assigneeId,
            linkedIssueId: data.linkedIssueId || data.issueId,
            // Ensure Timestamps are converted to Date objects
            dueDate: data.dueDate?.toDate?.() || data.dueDate || new Date(),
            completedDate: data.completedDate?.toDate?.() || data.completedDate,
            createdAt: data.createdAt || Timestamp.now(),
            updatedAt: data.updatedAt || Timestamp.now(),
          } as ActionItem;
        });

        // Remove rock-associated action items completely from the L10 view
        let filtered = actionItemsData.filter(item => !item.rockId && !item.linkedRockId);

        // Filter by overdue status (client-side)
        if (filters.overdueOnly) {
          const now = new Date();
          now.setHours(0, 0, 0, 0);
          filtered = filtered.filter((actionItem) => {
            const dueDate = new Date(actionItem.dueDate);
            dueDate.setHours(0, 0, 0, 0);
            return dueDate < now && !actionItem.complete;
          });
        }

        // Filter by link type (client-side)
        if (filters.linkType && filters.linkType !== 'all') {
          if (filters.linkType === 'issue') {
            filtered = filtered.filter((actionItem) => !!actionItem.linkedIssueId);
          } else if (filters.linkType === 'metric') {
            filtered = filtered.filter((actionItem) => !!actionItem.linkedMetricId);
          } else if (filters.linkType === 'standalone') {
            filtered = filtered.filter(
              (actionItem) =>
                !actionItem.linkedRockId && !actionItem.linkedIssueId && !actionItem.linkedMetricId && !actionItem.rockId
            );
          }
        }

        // Remove duplicates
        const uniqueItems = Array.from(new Map(filtered.map(item => [item.id, item])).values());
        setActionItems(uniqueItems);
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching action items: ', error);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [filters]); // Re-run the query when filters change

  const createActionItem = useCallback(
    async (formData: {
      description: string;
      dueDate: string | Date;
      ownerId?: string;
      assignedToUserId?: string;
      linkedRockId?: string | null;
      linkedIssueId?: string | null;
      linkedMetricId?: string | null;
      createdByUserId?: string;
      createdInMeetingId?: string;
    }) => {
      try {
        const now = Timestamp.now();
        const actionItemData = {
          description: formData.description.trim(),
          dueDate: formData.dueDate instanceof Date ? formData.dueDate : new Date(formData.dueDate),
          ownerId: formData.ownerId || null,
          assignedToUserId: formData.assignedToUserId || null,
          linkedRockId: formData.linkedRockId || null,
          linkedIssueId: formData.linkedIssueId || null,
          linkedMetricId: formData.linkedMetricId || null,
          complete: false,
          dropped: false,
          createdByUserId: formData.createdByUserId || '', // Required field
          createdInMeetingId: formData.createdInMeetingId || null,
          createdAt: now,
          updatedAt: now,
        };

        await addDoc(collection(db, COLLECTION_NAMES.ACTION_ITEMS), actionItemData);
      } catch (error) {
        console.error('Error creating action item:', error);
        throw error;
      }
    },
    []
  );

  const updateActionItem = useCallback(
    async (
      actionItemId: string,
      formData: Partial<{
        description: string;
        dueDate: string | Date;
        ownerId: string;
        assignedToUserId: string;
        linkedRockId: string | null;
        linkedIssueId: string | null;
        linkedMetricId: string | null;
      }>
    ) => {
      try {
        const actionItemRef = doc(db, COLLECTION_NAMES.ACTION_ITEMS, actionItemId);
        const updateData: any = {
          ...formData,
          updatedAt: Timestamp.now(),
        };

        // Convert dueDate to Date if it's a string
        if (updateData.dueDate && typeof updateData.dueDate === 'string') {
          updateData.dueDate = new Date(updateData.dueDate);
        }

        // Trim description if provided
        if (updateData.description) {
          updateData.description = updateData.description.trim();
        }

        await updateDoc(actionItemRef, updateData);
      } catch (error) {
        console.error('Error updating action item:', error);
        throw error;
      }
    },
    []
  );

  const completeActionItem = useCallback(async (actionItemId: string) => {
    try {
      const actionItemRef = doc(db, COLLECTION_NAMES.ACTION_ITEMS, actionItemId);
      await updateDoc(actionItemRef, {
        complete: true,
        completedDate: Timestamp.now(),
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error completing action item:', error);
      throw error;
    }
  }, []);

  const uncompleteActionItem = useCallback(async (actionItemId: string) => {
    try {
      const actionItemRef = doc(db, COLLECTION_NAMES.ACTION_ITEMS, actionItemId);
      await updateDoc(actionItemRef, {
        complete: false,
        completedDate: null,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error uncompleting action item:', error);
      throw error;
    }
  }, []);

  const dropActionItem = useCallback(async (actionItemId: string, reason?: string) => {
    try {
      const actionItemRef = doc(db, COLLECTION_NAMES.ACTION_ITEMS, actionItemId);
      await updateDoc(actionItemRef, {
        dropped: true,
        droppedReason: reason || null,
        updatedAt: Timestamp.now(),
      });
    } catch (error) {
      console.error('Error dropping action item:', error);
      throw error;
    }
  }, []);

  return {
    actionItems,
    loading,
    createActionItem,
    updateActionItem,
    completeActionItem,
    uncompleteActionItem,
    dropActionItem,
  };
}
